import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { AliasPageName } from 'src/app/gamification/cv-marketplace-admin/models/gamification-pages.model';
import { Budget, BudgetRecord, EmpGroup, GroupTypeRecord } from '../../models/budget.model';
import { BudgetService } from '../../services/budget/budget.service';
import { BudgetSetupService } from './service/budget-setup.service';
import { columnListBudgetSetup, headerBudgetSetup } from './export-butget-setup';
import { Component, HostListener, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { debounceTime, Subscription } from 'rxjs';
import { formatDate } from '@angular/common';
import { MenuItem } from '@niceltd/sol/floating-menu/src/lib/menu-items';
import { PointStorePermissionService } from 'src/app/gamification/marketplace/admin/services/point-store-permission.service';
import { SolToastrService } from '@niceltd/sol/toastr';
import { TypeFormats } from 'src/shared/types/exportItem';
import moment from 'moment';

@Component({
    selector: 'cv-admin-budget-setup',
    templateUrl: './cv-admin-budget-setup.component.html',
    styleUrls: ['./cv-admin-budget-setup.component.scss']
})
export class CvAdminBudgetSetupComponent implements OnInit, OnDestroy {

    editMode = true;
    budget: Budget = {
        budgetList: [],
        total: "",
        initBudgetList: []
    };

    fileName: string = "";

    defaultMenuOptions: MenuItem[] = [
        {
            label: "Excel",
            svgIconName: "icon-office-excel",
            callbackFn: this.onExportClick.bind(this),
        },
        {
            label: "CSV",
            svgIconName: "icon-CSV",
            callbackFn: this.onExportClick.bind(this),
        }
    ];

    groups: GroupTypeRecord[] = [];
    groupType: GroupTypeRecord;
    inputNumberMaxLength = 9;
    budgetForm: FormGroup;
    isLoading = false;
    isSaving = false;

    private subscriptionBudgetSetup: Subscription = new Subscription();

    constructor(
        @Inject(LOCALE_ID) private locale: string,
        private budgetService: BudgetService,
        private budgetSetupService: BudgetSetupService,
        private cvLocalizationService: CVLocalizationService,
        private fb: FormBuilder,
        private pointStorePermissionService: PointStorePermissionService,
        private toastrManager: SolToastrService,
    ) {
        this.budgetForm = this.fb.group({
            items: this.fb.array([]),
        });
    }

    get messageNotification(): string {
        return this.getLocalizationString("Budget_Warn_Notification", "Editing your Budget's group will make the total Budget start over");
    }

    get budgetPeriodMessage(): string {
        const periodLocalization = "New monthly budget starts today, ends {1} {0}, and repeats monthly unless changed.";
        const localString = this.getLocalizationString("Budget_Period_Info", periodLocalization);
        const endOfMonth = moment().endOf('month').toDate();
        const monthName = formatDate(endOfMonth, 'MMMM', this.locale);
        const day = formatDate(endOfMonth, 'd' , this.locale);
        return this.cvLocalizationService.Format(localString, `${this.formatOrdinals(this.locale, Number(day))}`, `${monthName}`);
    }

    get groupTypeName(): string {
        return this.groupType?.LocalizedName ?? this.getLocalizationString("Team", "Team");
    }

    get items(): FormArray {
        return this.budgetForm.get('items') as FormArray;
    }

    get sectionTitle(): string {
        return this.getLocalizationString("Budget_Setup", "Budget Setup");
    }

    @HostListener('window:beforeunload', ['$event'])
    validateUnsavedChanges(event: BeforeUnloadEvent) {
        if (!this.editMode) {
            event.preventDefault();
            event.returnValue = '';
            return;
        }
    }

    async ngOnInit() {
        this.subscriptionBudgetSetup = this.budgetSetupService.budgetSetup$.subscribe(value => {
            this.editMode = value.editMode;
        });

        await this.loadData();
        this.fileName = await this.cvLocalizationService.GetLocalText("Budget", "Budget_Setup", "Budget Setup");
    }

    ngOnDestroy(): void {
        this.subscriptionBudgetSetup.unsubscribe();
    }

    async loadData() {
        await this.withLoader(async () => {
            await Promise.all([
                await this.loadLocalization(),
                await this.loadGroups(),
                await this.loadBudget(),
            ]);
        });
    }

    async loadLocalization() {
        await this.cvLocalizationService.GetSection("Budget_Setup");
    }

    getLocalizationString(key: string, defaultValue: string) {
        return this.cvLocalizationService.getCachedText("Budget_Setup", key, defaultValue);
    }

    async loadBudget() {
        const budgetList = await this.budgetService.loadBudgets();
        this.budget.budgetList = budgetList?.map((budget) => ({
            ...budget,
            AmountString: budget.Amount.toString(),
        })).sort((a, b) => a.GroupLevelName.localeCompare(b.GroupLevelName, undefined, { sensitivity: 'base' }));
        this.budget.initBudgetList = [...this.budget.budgetList];
        this.reloadGroupType();
    }

    async loadGroups() {
        const groupType = await this.budgetService.loadGroupTypes();
        this.groups = groupType.EmpGroupTypes.filter(group => group.GroupLevel > 1)
            .map(group => ({
                ...group,
                LocalizedName: this.getLocalizationString(group.GroupTypeName, group.GroupTypeName),
            }));
    }

    async setDefaultSelectedGroup(group: GroupTypeRecord) {
        this.groupType = group;
        await this.filterGroupsByLevel();
    }

    async onExportClick(item: MenuItem) {
        const formatType: TypeFormats = item.label === "Excel" ? "excel" : "csv";
        const { thousandSeparator } = this.getNumberSeparators(CVCultureName);
        const body = {
            columnList: columnListBudgetSetup(this.groupTypeName),
            headerItems: headerBudgetSetup(this.budget.total.replace(new RegExp(`\\${thousandSeparator}`, 'g'), ''), CVCultureName),
            values: this.getBudgetSetupItems(),
            format: formatType,
            fileNameExport: {
                "name": "Budget_Setup",
                "section": "Budget"
            },
            fileName: this.fileName,
            notification: {
                "section": "Budget",
                "field": "Budget_Setup",
                "defaultMessage": "Budget Setup"
            },
        }
        await this.budgetService.exportBudgetDefault(formatType, body, this.fileName);
    }

    getNumberSeparators(locale: string): { thousandSeparator: string, decimalSeparator: string } {
        // any number with group and decimal separators
        const numberWithGroupAndDecimalSeparators = 1000.1;
        const formattedNumber = new Intl.NumberFormat(locale).formatToParts(numberWithGroupAndDecimalSeparators);
        const thousandSeparator = formattedNumber.find(part => part.type === 'group')?.value || ',';
        const decimalSeparator = formattedNumber.find(part => part.type === 'decimal')?.value || '.';
        return { thousandSeparator, decimalSeparator };
    }

    createForm() {
        this.items.clear();
        this.budget.budgetList.forEach(item => {
            this.items.push(
                this.fb.group({
                    BudgetID: [item.BudgetID],
                    GroupLevelName: [item.GroupLevelName],
                    GroupLevel: [item.GroupLevel],
                    EmpGroupID: [item.EmpGroupID],
                    Amount: [item.Amount],
                    AmountString: [Number(item.AmountString).toLocaleString(CVCultureName)],
                })
            );
        });
    }

    async changeBudget() {
        const hasCreatePermission: boolean = await this.pointStorePermissionService.validatePermission({
            name: this.pointStorePermissionService.getCreatePermissionName(),
            pageName: this.pointStorePermissionService.pageNameFilter(AliasPageName.BudgetTab),
            newCXoneModalVersion: true
        });
        if (!hasCreatePermission) {
            return;
        }

        this.budgetSetupService.budgetSetup.next({
            ...this.budgetSetupService.budgetSetup.value,
            editMode: false
        });
        this.toogleBudgetForm();
    }

    async save() {
        if (this.isSaving) return;
        this.isSaving = true;
        const hasCreatePermission: boolean = await this.pointStorePermissionService.validatePermission({
            name: this.pointStorePermissionService.getCreatePermissionName(),
            pageName: this.pointStorePermissionService.pageNameFilter(AliasPageName.BudgetTab),
            newCXoneModalVersion: true
        });
        if (!hasCreatePermission) {
            this.isSaving = false;
            return;
        }
        try {
            const { items: budget } = this.budgetForm.value;
            const { status_code } = await this.budgetService.saveBudget(budget);
            if (status_code === 200) {
                await this.loadBudget();
                this.budgetSetupService.budgetSetup.next({
                    ...this.budgetSetupService.budgetSetup.value,
                    editMode: true
                });
                this.toastrManager.showToaster('success', this.getLocalizationString("Applied_Successfully", "Applied successfully."));
            }
        } finally {
            this.isSaving = false;
        }
    }

    async cancel() {
        this.budgetSetupService.onBudgetSetupUnsavedChanges(() => {
            this.groupType.initialGroupLevel = this.groupType.GroupLevel;
            this.budgetSetupService.budgetSetup.next({
                ...this.budgetSetupService.budgetSetup.value,
                editMode: true
            });
            this.reloadGroupType();
        });
    }

    formatNumber(event: Event, index: number, formControl: AbstractControl) {
        const inputElement = event.target as HTMLInputElement;
        let coinValue = inputElement.value.replace(/\D/g, '');
        if (coinValue.length > this.inputNumberMaxLength) {
            coinValue = coinValue.substring(0, this.inputNumberMaxLength);
        }
        const numericValue = Number(coinValue);
        const formattedValue = numericValue.toLocaleString(CVCultureName);
        inputElement.value = formattedValue;
        if (formControl) {
            formControl.setValue(formattedValue);
            this.items.at(index).get('Amount')?.setValue(numericValue);
        }
    }

    async onSelectGroupLevel(groupType: GroupTypeRecord) {
        this.withLoader(async () => {
            this.groupType = groupType;
            await this.filterGroupsByLevel();
        });
    }

    async filterGroupsByLevel() {
        const level = this.groupType.initialGroupLevel ?? this.groupType.GroupLevel;
        const empGroups: EmpGroup[] = await this.budgetService.loadGroupsByLevel(level);
        const budgetList: BudgetRecord[] = empGroups.map((empGroup) => {
            const groupLvlFilt = (item) => (item.EmpGroupID === empGroup.groupDTO.GroupID && item.GroupLevel === this.groupType.GroupLevel);
            const budget = this.budget.initBudgetList.find(groupLvlFilt);
            return {
                BudgetID: budget?.BudgetID,
                CompanyID: empGroup.groupDTO.CompanyID,
                GroupLevel: empGroup.groupDTO.GroupLevel,
                GroupLevelName: empGroup.groupDTO.GroupName,
                EmpGroupID: empGroup.groupDTO.GroupID,
                Amount: budget?.Amount ?? 0,
                AmountString: budget?.Amount.toString() ?? "0",
                PeriodStartDate: budget?.PeriodStartDate,
                CoinsAwarded: budget?.CoinsAwarded,
                AwardedPercentage: budget?.AwardedPercentage,
                NotifyLogins: budget?.NotifyLogins,
            };
        });
        this.budget.budgetList = budgetList;
        this.createForm();
        this.toogleBudgetForm();
        this.getBudgetTotal();
        this.onBudgetFormChanges();
    }

    getBudgetTotal() {
        const total = this.items.controls.map(control => control.get('Amount')?.value || 0)
            .reduce((acc, amount) => acc + amount, 0);
        this.budget.total = total.toLocaleString(CVCultureName);
    }

    toogleBudgetForm() {
        this.items.controls.forEach(control => {
            if (this.editMode) {
                control.disable();
            } else {
                control.enable();
            }
        });
    }

    onBudgetFormChanges() {
        this.items.controls.forEach((control, index) => {
            control.valueChanges.pipe(debounceTime(300)).subscribe(value => {
                this.getBudgetTotal();
            });
        });
    }

    async reloadGroupType() {
        const validateGroupLevel = new Set(this.budget.initBudgetList?.map(budget => budget.GroupLevel));
        const filteredGroup = this.groups.find(group => group.GroupLevel === validateGroupLevel.values().next().value);
        const groupLevel = validateGroupLevel?.size === 1 ? filteredGroup : this.groups[0];
        this.budgetSetupService.budgetSetup.next({
            ...this.budgetSetupService.budgetSetup.value,
            selectedGroup: groupLevel
        });
        await this.setDefaultSelectedGroup(groupLevel);
    }

    async withLoader(task: () => Promise<void> | void) {
        this.isLoading = true;
        try {
            const res = task();
            if (res instanceof Promise) {
                await res;
            }
        } finally {
            this.isLoading = false;
        }
    }

    getBudgetSetupItems() {
        const setupItems = this.budget.budgetList?.map(data => ({
            [this.groupTypeName]: data.GroupLevelName,
            Budget_Distribution: `${data.Amount} ${this.getLocalizationString("Coins", "Coins")}`
        })).sort((a, b) => {
            if ((a[this.groupTypeName]).toLowerCase() < (b[this.groupTypeName].toLowerCase())) {
                return -1;
            } else if ((a[this.groupTypeName].toLowerCase()) > (b[this.groupTypeName].toLowerCase())) {
                return 1;
            } else {
                return 0;
            }
        });

        return setupItems;
    }

    formatOrdinals(locale: string, day: number): string {
        const ordinalRules = new Intl.PluralRules(locale, { type: 'ordinal' });
        const suffixes = new Map([
            ["one", this.getLocalizationString("St_Ordinal_Indicator", "st")],
            ["other", this.getLocalizationString("Th_Ordinal_Indicator", "th")],
        ]);
        const rule = ordinalRules.select(day);
        const suffix = suffixes.get(rule) || '';
        return `${this.cvLocalizationService.Format(suffix, day)}`;
    };

    hasPermission(permission: string): boolean {
        return this.pointStorePermissionService.checkPermission(permission,
            this.pointStorePermissionService.pageNameFilter(AliasPageName.BudgetTab),
        );
    }
}
